import type { CanvasSectionBlock, CanvasSectionSettings, HomepageSectionSettings } from '../sectionRegistry';

export const SECTION_ROW_HEIGHT_PX = 36;
export const SECTION_MIN_ROWS_MIN = 8;
export const SECTION_MIN_ROWS_MAX = 192;
export const SECTION_MIN_ROWS_DEFAULT = 12;
export const COMPACT_SECTION_MIN_ROWS = 2;
export const MANUAL_SECTION_MIN_ROWS = COMPACT_SECTION_MIN_ROWS;

type ResolveSectionRowsOptions = {
  floorRows?: number;
  fallbackRows?: number;
  maxRows?: number;
};

export function isCompactCanvasSectionBlockSet(blocks: CanvasSectionBlock[]): boolean {
  return blocks.length > 0 && blocks.every((block) => block.type === 'site-progress');
}

export function resolveCompactCanvasFloorRows(
  blocks: CanvasSectionBlock[],
  fallbackFloorRows: number
) {
  return isCompactCanvasSectionBlockSet(blocks)
    ? COMPACT_SECTION_MIN_ROWS
    : fallbackFloorRows;
}

function resolveSectionRowOptions(
  blocks: CanvasSectionBlock[],
  options: ResolveSectionRowsOptions = {}
): ResolveSectionRowsOptions {
  return {
    floorRows: resolveCompactCanvasFloorRows(
      blocks,
      Math.max(1, Math.round(options.floorRows ?? SECTION_MIN_ROWS_DEFAULT))
    ),
    fallbackRows: resolveCompactCanvasFloorRows(
      blocks,
      Math.max(1, Math.round(options.fallbackRows ?? SECTION_MIN_ROWS_DEFAULT))
    ),
    maxRows: options.maxRows,
  };
}

export function resolveSectionMinRowsOptionsForMode(
  blocks: CanvasSectionBlock[],
  options: ResolveSectionRowsOptions = {},
  mode: CanvasSectionSettings['minRowsMode'] = 'auto'
): ResolveSectionRowsOptions {
  const resolvedOptions = resolveSectionRowOptions(blocks, options);
  if (mode !== 'manual') return resolvedOptions;
  return {
    ...resolvedOptions,
    floorRows: Math.min(
      Math.max(1, Math.round(resolvedOptions.floorRows ?? SECTION_MIN_ROWS_DEFAULT)),
      MANUAL_SECTION_MIN_ROWS
    ),
  };
}

function shouldResetCompactSectionMinRows(
  blocks: CanvasSectionBlock[],
  value: unknown,
  options: ResolveSectionRowsOptions = {}
): boolean {
  if (!isCompactCanvasSectionBlockSet(blocks)) return false;
  const nextValue = Number(value);
  if (!Number.isFinite(nextValue)) return false;
  const roundedValue = Math.round(nextValue);
  const legacyCandidates = new Set<number>([
    Math.max(1, Math.round(options.floorRows ?? SECTION_MIN_ROWS_MIN)),
    Math.max(1, Math.round(options.fallbackRows ?? SECTION_MIN_ROWS_DEFAULT)),
    SECTION_MIN_ROWS_MIN,
    SECTION_MIN_ROWS_DEFAULT,
  ]);

  legacyCandidates.delete(COMPACT_SECTION_MIN_ROWS);

  return legacyCandidates.has(roundedValue);
}

export function getSectionCanvasBlocks(settings?: HomepageSectionSettings): CanvasSectionBlock[] {
  return Array.isArray(settings?.canvas?.blocks) ? settings!.canvas!.blocks : [];
}

export function normalizeCanvasGridResolution(
  canvas: CanvasSectionSettings | undefined,
  options: ResolveSectionRowsOptions = {}
): CanvasSectionSettings {
  const source: CanvasSectionSettings = canvas && typeof canvas === 'object'
    ? canvas
    : { blocks: [] };
  const blocks = Array.isArray(source.blocks) ? source.blocks : [];

  return {
    minRows: resolveCanvasMinRows(blocks, source.minRows, options, source.minRowsMode),
    minRowsMode: source.minRowsMode === 'manual' ? 'manual' : 'auto',
    presentation: source.presentation === 'immersive' ? 'immersive' : 'default',
    blocks,
  };
}

export function enforceCanvasContentMinRows(
  canvas: CanvasSectionSettings,
  options: ResolveSectionRowsOptions = {}
): CanvasSectionSettings {
  if (canvas.presentation === 'immersive') return canvas;

  const blocks = Array.isArray(canvas.blocks) ? canvas.blocks : [];
  const mode: CanvasSectionSettings['minRowsMode'] =
    canvas.minRowsMode === 'manual' ? 'manual' : 'auto';
  const resolvedOptions = resolveSectionMinRowsOptionsForMode(blocks, options, mode);
  const currentMinRows = resolveCanvasMinRows(blocks, canvas.minRows, options, mode);
  const contentRows = resolveSectionExtentRows(
    blocks,
    resolvedOptions.floorRows ?? currentMinRows
  );
  const nextMinRows =
    mode === 'manual'
      ? Math.max(currentMinRows, contentRows)
      : contentRows;

  return {
    ...canvas,
    minRows: clampSectionMinRows(nextMinRows, {
      floorRows: nextMinRows,
      fallbackRows: nextMinRows,
      maxRows: resolvedOptions.maxRows,
    }),
    minRowsMode: mode,
  };
}

export function resolveCanvasMinRows(
  blocks: CanvasSectionBlock[],
  value: unknown,
  options: ResolveSectionRowsOptions = {},
  mode: CanvasSectionSettings['minRowsMode'] = 'auto'
): number {
  const resolvedOptions = resolveSectionMinRowsOptionsForMode(blocks, options, mode);
  const normalizedValue =
    mode === 'manual'
      ? value
      : shouldResetCompactSectionMinRows(blocks, value, options)
        ? undefined
        : value;
  return clampSectionMinRows(normalizedValue, resolvedOptions);
}

export function clampSectionMinRows(
  value: unknown,
  options: ResolveSectionRowsOptions = {}
): number {
  const floorRows = Math.max(
    1,
    Math.round(options.floorRows ?? SECTION_MIN_ROWS_DEFAULT)
  );
  const maxRows = Math.max(
    floorRows,
    Math.round(options.maxRows ?? SECTION_MIN_ROWS_MAX)
  );
  const fallbackRows = Math.max(
    floorRows,
    Math.min(maxRows, Math.round(options.fallbackRows ?? SECTION_MIN_ROWS_DEFAULT))
  );
  const nextValue = Number(value);
  if (!Number.isFinite(nextValue)) return fallbackRows;
  return Math.max(floorRows, Math.min(maxRows, Math.round(nextValue)));
}

export function resolveSectionExtentRows(
  blocks: CanvasSectionBlock[],
  floorRows: number
): number {
  return blocks.reduce((max, block) => {
    const content =
      block.content && typeof block.content === 'object'
        ? (block.content as Record<string, unknown>)
        : {};
    const fallbackScale =
      block.type === 'product-card'
        ? Math.max(0.5, Math.min(1.25, Number(content.contentScale) || 1))
        : 1;
    const scaleY =
      block.type === 'product-card'
        ? Math.max(0.5, Math.min(1.25, Number(content.contentScaleY) || fallbackScale))
        : 1;
    const designRows =
      block.type === 'product-card'
        ? Math.max(1, Number(content.designRows) || Math.round(block.layout.h))
        : Math.round(block.layout.h);
    const autoSizeResolved =
      block.type === 'product-card' ? content.autoSizeResolved !== false : true;
    const scaledBaseHeight =
      block.type === 'product-card'
        ? Math.max(
            Math.round(block.layout.h),
            autoSizeResolved && scaleY <= 1
              ? Math.round(block.layout.h)
              : Math.round(designRows)
          )
        : Math.round(block.layout.h);
    const effectiveHeight =
      block.type === 'product-card'
        ? Math.max(
            1,
            Math.ceil(scaledBaseHeight * scaleY)
          )
        : Math.round(block.layout.h);
    const end = Math.round(block.layout.y) + effectiveHeight;
    return Number.isFinite(end) ? Math.max(max, end) : max;
  }, floorRows);
}

export function resolveSectionRows(
  settings: HomepageSectionSettings | undefined,
  options: ResolveSectionRowsOptions = {}
): number {
  const blocks = getSectionCanvasBlocks(settings);
  const minRows = resolveCanvasMinRows(
    blocks,
    settings?.canvas?.minRows,
    options,
    settings?.canvas?.minRowsMode
  );
  if (settings?.canvas?.presentation === 'immersive') {
    return minRows;
  }
  return Math.max(minRows, resolveSectionExtentRows(blocks, minRows));
}

export function resolveSectionMinHeightPx(
  settings: HomepageSectionSettings | undefined,
  options: ResolveSectionRowsOptions = {}
): number {
  return resolveSectionRows(settings, options) * SECTION_ROW_HEIGHT_PX;
}
